import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { getApi } from "../../api/api";
import { EmailStage } from "../../common/types/enums";
import { PROFILE_ROUTE } from "../../common/utils/constants";

const isValidEmail = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email) && email.length >= 5;
};

export const useLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [stage, setStage] = useState<EmailStage>(EmailStage.EMAIL_INPUT);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [legalityChecked, setLegalityChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [timer, setTimer] = useState(0);
  const [registerMessage, setRegisterMessage] = useState("");

  useEffect(() => {
    if (timer <= 0) return;
    const id = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [timer]);

  const afterAuthRoute =
    (location.state as { from?: { pathname: string } } | null)?.from
      ?.pathname || PROFILE_ROUTE;

  const requestCode = useCallback(async (email: string) => {
    if (!isValidEmail(email)) {
      setMessage("Введите корректный email");
      setStage(EmailStage.ERROR);
      return;
    }
    try {
      setLoading(true);
      setMessage("Отправка кода…");
      await getApi().requestEmailCode({ email });
      setEmail(email);
      setCode("");
      setTimer(60);
      setMessage("");
      setRegisterMessage("");
      setStage(EmailStage.CODE_INPUT);
    } catch (error: any) {
      setMessage(error?.response?.data?.message || "Ошибка отправки кода");
      setStage(EmailStage.ERROR);
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyCode = useCallback(
    async (code: string) => {
      try {
        setLoading(true);
        setMessage("Проверка кода…");
        const result = await getApi().verifyEmailCode({ email, code });

        if (result.registerAction) {
          setIsLogin(false);
          setRegisterMessage(
            "Аккаунт не найден. Подтвердите согласие с документами и нажмите «Зарегистрироваться»",
          );
          setMessage("");
          return;
        }

        if (result.accessToken) {
          navigate(afterAuthRoute, { replace: true });
        }
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401) setMessage("Неверный или просроченный код");
        else if (status === 429)
          setMessage("Слишком много попыток, попробуйте позже");
        else if (error?.code === "ERR_NETWORK") setMessage("Нет подключения");
        else
          setMessage(error?.response?.data?.message || "Ошибка проверки кода");
      } finally {
        setLoading(false);
      }
    },
    [email, navigate, afterAuthRoute],
  );

  const register = useCallback(async () => {
    if (!legalityChecked || loading) return;
    try {
      setLoading(true);
      setMessage("Регистрация…");
      const result = await getApi().register({ email, code });
      if (result.accessToken) {
        navigate(afterAuthRoute, { replace: true });
      }
    } catch (error: any) {
      setMessage(error?.response?.data?.message || "Ошибка регистрации");
    } finally {
      setLoading(false);
    }
  }, [email, code, legalityChecked, loading, navigate, afterAuthRoute]);

  const resendCode = useCallback(() => {
    if (email && timer === 0) requestCode(email);
  }, [email, timer, requestCode]);

  const toggleLegality = useCallback(() => setLegalityChecked((v) => !v), []);

  return {
    stage,
    loading,
    message,
    registerMessage,
    isLogin,
    legalityChecked,
    email,
    code,
    timer,
    setCode,
    requestCode,
    verifyCode,
    register,
    resendCode,
    toggleLegality,
    setStage,
    isValidEmail,
  };
};
